import type { IconName } from '../../../components/icons';
import type { AppUserRole, ShelterRosterRole } from '../../../constants/appUserRole';
import { ROUTES } from '../../../constants/routes';
import { isShelterAdministrator, isSuperAdmin } from '../../../lib/rbac';

export type SettingsAudience = 'all' | 'org_admin' | 'shelter';

export type SettingsNavGroupId = 'user' | 'friend' | 'legal';

export type SettingsNavItem = {
  id: string;
  href: string;
  label: string;
  group: SettingsNavGroupId;
  icon: IconName;
  audience: SettingsAudience;
};

export const settingsNavItems: SettingsNavItem[] = [
  {
    id: 'account',
    href: ROUTES.adminSettingsAccount,
    label: 'My account',
    group: 'user',
    icon: 'user-circle-outline',
    audience: 'all',
  },
  {
    id: 'shelter-info',
    href: ROUTES.adminSettingsShelterInfo,
    label: 'Shelter info',
    group: 'friend',
    icon: 'building-outline',
    audience: 'shelter',
  },
  {
    id: 'subscription',
    href: ROUTES.adminSettingsSubscription,
    label: 'Subscription',
    group: 'user',
    icon: 'credit-card-outline',
    audience: 'org_admin',
  },
  {
    id: 'billing',
    href: ROUTES.adminSettingsBilling,
    label: 'Billing',
    group: 'user',
    icon: 'currency-sync-outline',
    audience: 'org_admin',
  },
  {
    id: 'customization',
    href: ROUTES.adminSettingsCustomization,
    label: 'Customization',
    group: 'friend',
    icon: 'paint-roller-outline',
    audience: 'org_admin',
  },
  {
    id: 'quiz',
    href: ROUTES.adminSettingsQuiz,
    label: 'Quiz',
    group: 'friend',
    icon: 'help-square-outline',
    audience: 'org_admin',
  },
  {
    id: 'shelter-crm',
    href: ROUTES.adminSettingsShelterCrm,
    label: 'Shelter CRM',
    group: 'friend',
    icon: 'clipboard-search-outline',
    audience: 'org_admin',
  },
  {
    id: 'terms',
    href: ROUTES.adminSettingsTerms,
    label: 'Terms and conditions',
    group: 'legal',
    icon: 'file-text-outline',
    audience: 'all',
  },
];

export const settingsGroupLabels: Record<Exclude<SettingsNavGroupId, 'legal'>, string> = {
  user: 'User settings',
  friend: 'Friend settings',
};

/** Shelter org settings — not shown to platform super admins. */
export function canAccessSettingsNavItem(
  audience: SettingsAudience,
  role: AppUserRole,
  shelterRole?: ShelterRosterRole,
  shelterId?: string,
): boolean {
  if (audience === 'all') return true;
  if (audience === 'shelter') {
    if (isSuperAdmin(role)) return false;
    return Boolean(shelterId);
  }
  if (isSuperAdmin(role)) return false;
  return isShelterAdministrator(role, shelterRole);
}
